/**
 * Sidebar "Yeni" rozeti — pill (genişletilmiş) + dot (daraltılmış/ikon) varyantı (#770).
 *
 * NotificationBell (`components/notifications/notification-bell.tsx`) desenini
 * izler: marka chartreuse zemin (amber, KIRMIZI DEĞİL) + framer-motion pulse.
 * Fark: NotificationBell'in pulse'ı TEK-SEFER (yeni bildirimde), buradaki
 * SÜREKLİ ama yavaş (~2.4s) nefes alan halo — "yeni özellik var" sinyali süre
 * boyunca (newUntil'e kadar) geçerli kalmalı, tek seferlik değil.
 *
 * prefers-reduced-motion'a saygılı: azaltılmış hareket açıkken halo/pulse
 * KAPALI, rozet statik kalır (görünürlüğü etkilenmez).
 */
import { motion, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'

const GLOW_TRANSITION = {
  duration: 2.4,
  repeat: Infinity,
  ease: 'easeInOut',
} as const

const GLOW_BOX_SHADOW = [
  '0 0 0 0 hsl(var(--brand-chartreuse) / 0)',
  '0 0 6px 2px hsl(var(--brand-chartreuse) / 0.55)',
  '0 0 0 0 hsl(var(--brand-chartreuse) / 0)',
]

/** sr-only erişilebilir etiket — görsel rozet yalnız "Yeni" yazsa da ekran
 *  okuyucu ne olduğunu netleştirsin. */
const SR_LABEL = 'yeni özellik'

type NavNewBadgeProps = {
  className?: string
  /**
   * Rozet zaten aria-label'ı ele geçiren bir üst öğe (ör. nav link) içindeyse
   * `true` geçilir — kendi sr-only/aria-label'ı çift okunmayı önlemek için
   * bastırılır (üst öğenin aria-label'ı "yeni özellik" ifadesini zaten içerir).
   */
  'aria-hidden'?: boolean | 'true' | 'false'
}

/** Genişletilmiş sidebar'da nav item satırının sonunda görünen küçük pill. */
export function NavNewPill({ className, 'aria-hidden': ariaHidden }: NavNewBadgeProps) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <motion.span
      aria-hidden={ariaHidden}
      lang="tr"
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5',
        'bg-brand-chartreuse text-[9px] font-bold uppercase leading-none tracking-wide text-brand-chartreuse-foreground',
        className,
      )}
      animate={prefersReducedMotion ? undefined : { boxShadow: GLOW_BOX_SHADOW }}
      transition={prefersReducedMotion ? undefined : GLOW_TRANSITION}
    >
      Yeni
      {!ariaHidden && <span className="sr-only"> ({SR_LABEL})</span>}
    </motion.span>
  )
}

/** Daraltılmış sidebar (ikon-only rail) veya kapalı bölüm başlığında görünen
 *  küçük parlayan nokta — pill'in yer olmadığı yerlerde aynı sinyali verir. */
export function NavNewDot({ className, 'aria-hidden': ariaHidden }: NavNewBadgeProps) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <span
      className={cn('relative inline-flex size-2', className)}
      {...(ariaHidden
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': SR_LABEL })}
    >
      {!prefersReducedMotion && (
        <motion.span
          className="absolute inset-0 rounded-full bg-brand-chartreuse"
          animate={{ opacity: [0.7, 0, 0.7], scale: [1, 1.8, 1] }}
          transition={GLOW_TRANSITION}
        />
      )}
      <span className="relative inline-flex size-2 rounded-full bg-brand-chartreuse" />
    </span>
  )
}
